/** V4 hotbar: 4 slots for Active skills. Tree selection: commit to 3 of 9 trees. */
import Phaser from 'phaser';
import { Component } from './Component';
import { ConfigLoader } from '../systems/ConfigLoader';
import type { SkillProgressionComponent } from './SkillProgressionComponent';

const SLOT_COUNT = 4;
const MAX_SELECTED_TREES = 3;

/** Manages the player's 4 hotbar slots and 3-of-9 tree selection commitment. */
export class AttunementComponent implements Component {
  private _slotAssignments: (string | null)[];
  private _selectedTrees: Set<string>;
  private events: Phaser.Events.EventEmitter;
  private skillProgression: SkillProgressionComponent;

  constructor(events: Phaser.Events.EventEmitter, skillProgression: SkillProgressionComponent) {
    this.events = events;
    this.skillProgression = skillProgression;
    this._slotAssignments = this.buildDefaultSlots();
    this._selectedTrees = new Set(ConfigLoader.getDefaultSelectedTrees());
  }

  /** Build default slot array: foundation skills (evolved if applicable) in order, then nulls. */
  private buildDefaultSlots(): (string | null)[] {
    const foundation = ConfigLoader.v4Hotbar.foundationSkills as readonly string[];
    const slots: (string | null)[] = foundation.map(
      id => this.skillProgression.getEffectiveSkill(id)
    );
    while (slots.length < SLOT_COUNT) slots.push(null);
    return slots.slice(0, SLOT_COUNT);
  }

  get slotCount(): number { return SLOT_COUNT; }

  /** Return the list of non-null skill IDs currently in the hotbar. */
  get attunedSkills(): readonly string[] {
    return this._slotAssignments.filter((s): s is string => s !== null);
  }

  /** Total slots is always 4 in V4. */
  get totalSlots(): number { return SLOT_COUNT; }

  /** Used slots = non-null entries. */
  get usedSlots(): number {
    return this._slotAssignments.filter(s => s !== null).length;
  }

  /** Available slots = null entries. */
  get availableSlots(): number {
    return SLOT_COUNT - this.usedSlots;
  }

  /** Return true if the skill is in the hotbar or is a passive (always-on). */
  isAttuned(skillId: string): boolean {
    // Check if evolution replaces this skill — use effective skill
    const effective = this.skillProgression.getEffectiveSkill(skillId);
    if (this._slotAssignments.includes(effective)) return true;
    if (this._slotAssignments.includes(skillId)) return true;

    // Passives are always-on once unlocked — but only from selected trees
    const node = ConfigLoader.getV4Node(skillId);
    if (node && node.nodeType === 'passive' && this.skillProgression.isUnlocked(skillId)) {
      const tree = ConfigLoader.getV4SkillTree(skillId);
      return tree !== undefined && this._selectedTrees.has(tree);
    }

    return false;
  }

  /** Return true if the skill can be placed in the hotbar. Tree must be selected. */
  canAttune(skillId: string): boolean {
    if (!this.skillProgression.isUnlocked(skillId)) return false;
    const node = ConfigLoader.getV4Node(skillId);
    if (!node) return false;
    if (node.nodeType !== 'active' && node.nodeType !== 'evolution') return false;
    if (this._slotAssignments.includes(skillId)) return false;
    // Block attuning a skill that has been replaced by an unlocked evolution
    if (this.skillProgression.getEffectiveSkill(skillId) !== skillId) return false;
    const tree = ConfigLoader.getV4SkillTree(skillId);
    if (tree !== undefined && !this._selectedTrees.has(tree)) return false;
    return this.availableSlots > 0;
  }

  /** Auto-place: put skill in first available slot. */
  attune(skillId: string): boolean {
    if (!this.canAttune(skillId)) return false;
    for (let i = 0; i < SLOT_COUNT; i++) {
      if (this._slotAssignments[i] === null) {
        this._slotAssignments[i] = skillId;
        this.emitChanged();
        return true;
      }
    }
    return false;
  }

  /** Place a skill in a specific slot. */
  attuneToSlot(skillId: string, slotIndex: number): boolean {
    if (!this.canAttune(skillId)) return false;
    if (slotIndex < 0 || slotIndex >= SLOT_COUNT) return false;
    if (this._slotAssignments[slotIndex] !== null) return false;
    this._slotAssignments[slotIndex] = skillId;
    this.emitChanged();
    return true;
  }

  /** Remove a skill from its slot. */
  unattune(skillId: string): void {
    const idx = this._slotAssignments.indexOf(skillId);
    if (idx >= 0) {
      this._slotAssignments[idx] = null;
      this.emitChanged();
    }
  }

  /** Clear all slots and tree selection, rebuild with defaults. */
  respec(): void {
    this._slotAssignments = this.buildDefaultSlots();
    this._selectedTrees = new Set(ConfigLoader.getDefaultSelectedTrees());
    this.emitTreeSelectionChanged();
    this.emitChanged();
  }

  /** Replace a skill in its slot with a new one (for evolution). */
  replaceSkill(oldSkillId: string, newSkillId: string): void {
    const idx = this._slotAssignments.indexOf(oldSkillId);
    if (idx >= 0) {
      this._slotAssignments[idx] = newSkillId;
      this.emitChanged();
    }
  }

  /** Return the skill ID occupying the given slot index, or null if empty. */
  getSkillAtSlot(index: number): string | null {
    if (index < 0 || index >= SLOT_COUNT) return null;
    return this._slotAssignments[index];
  }

  /** Returns indices of empty slots. */
  getEmptySlots(): number[] {
    const empty: number[] = [];
    for (let i = 0; i < SLOT_COUNT; i++) {
      if (this._slotAssignments[i] === null) empty.push(i);
    }
    return empty;
  }

  private emitChanged(): void {
    this.events.emit('attunement-changed', {
      attunedSkills: [...this.attunedSkills],
      totalSlots: SLOT_COUNT,
      usedSlots: this.usedSlots,
    });
  }

  // --- Tree Selection ---

  /** Return true if the tree is one of the player's 3 selected trees. */
  isTreeSelected(treeName: string): boolean {
    return this._selectedTrees.has(treeName);
  }

  /** Return the currently selected tree names. */
  getSelectedTrees(): readonly string[] {
    return [...this._selectedTrees];
  }

  /** Select a tree (max 3). Returns true if selection changed. */
  selectTree(treeName: string): boolean {
    if (this._selectedTrees.has(treeName)) return false;
    if (this._selectedTrees.size >= MAX_SELECTED_TREES) return false;
    this._selectedTrees.add(treeName);
    this.emitTreeSelectionChanged();
    return true;
  }

  /** Deselect a tree. Auto-unattunes all skills from that tree. Returns true if selection changed. */
  deselectTree(treeName: string): boolean {
    if (!this._selectedTrees.has(treeName)) return false;
    this._selectedTrees.delete(treeName);

    // Unattune all hotbar skills from the deselected tree
    for (let i = 0; i < SLOT_COUNT; i++) {
      const skillId = this._slotAssignments[i];
      if (skillId && ConfigLoader.getV4SkillTree(skillId) === treeName) {
        this._slotAssignments[i] = null;
      }
    }

    this.emitTreeSelectionChanged();
    this.emitChanged();
    return true;
  }

  /** Export selected trees for saving. */
  exportSelectedTrees(): string[] {
    return [...this._selectedTrees];
  }

  /** Import saved tree selection. Falls back to defaults if empty. */
  importSelectedTrees(trees: string[]): void {
    if (trees.length === 0) {
      this._selectedTrees = new Set(ConfigLoader.getDefaultSelectedTrees());
    } else {
      this._selectedTrees = new Set(trees.slice(0, MAX_SELECTED_TREES));
    }
    this.emitTreeSelectionChanged();
  }

  private emitTreeSelectionChanged(): void {
    this.events.emit('tree-selection-changed', {
      selectedTrees: [...this._selectedTrees],
    });
  }

  /** Export hotbar for saving. */
  exportHotbar(): (string | null)[] {
    return [...this._slotAssignments];
  }

  /** Import saved hotbar — restores exact slot positions without rebuilding defaults. */
  importHotbar(slots: (string | null)[]): void {
    this._slotAssignments = new Array(SLOT_COUNT).fill(null);
    for (let i = 0; i < Math.min(slots.length, SLOT_COUNT); i++) {
      this._slotAssignments[i] = slots[i];
    }
    this.emitChanged();
  }

  /** @deprecated V3 compat — export attuned skill IDs. */
  exportAttuned(): string[] {
    return [...this.attunedSkills];
  }

  /** @deprecated V3 compat — import from skill ID list. */
  importAttuned(skills: string[]): void {
    this._slotAssignments = new Array(SLOT_COUNT).fill(null);
    for (const skillId of skills) {
      if (this._slotAssignments.includes(skillId)) continue;
      for (let i = 0; i < SLOT_COUNT; i++) {
        if (this._slotAssignments[i] === null) {
          this._slotAssignments[i] = skillId;
          break;
        }
      }
    }
    this.emitChanged();
  }

  update(_delta: number): void {
    // No per-frame logic
  }

  destroy(): void {
    // Nothing to clean up
  }
}
